import { useEffect, useRef } from "react";

/**
 * Closes a comment card on a press anywhere outside it. Outside means outside
 * its React tree, not its DOM, so its portaled menus count as inside: a blur
 * check would close the card as one of them opens. Spread the returned props
 * on the card and on its trigger. Null while closed.
 */
export function useCloseOnPressOutside(close: (() => void) | null) {
  // Set as the press passes through the card, then read by the document listener, which hears it last.
  const isInside = useRef(false);
  const closeRef = useRef(close);
  closeRef.current = close;
  const isOpen = close !== null;
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = () => {
      if (!isInside.current) closeRef.current?.();
      isInside.current = false;
    };
    // A press in a frame, like an email's body, never reaches this document; the focus it takes does.
    const onWindowBlur = () => {
      if (document.activeElement instanceof HTMLIFrameElement)
        closeRef.current?.();
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [isOpen]);
  return {
    onPointerDown: () => {
      isInside.current = true;
    },
  };
}
